/**
 * Lightweight Most-Recently-Used registry persisted to localStorage.
 * Used by the Command Palette and dashboards to surface what the user
 * touched most recently.
 */

const KEY = "lovable.recents.v1";
const MAX_PER_KIND = 8;

export type RecentKind = "report" | "site" | "document" | "action";

export interface RecentItem {
  kind: RecentKind;
  id: string;
  label: string;
  sublabel?: string;
  href: string;
  ts: number;
}

type Bucket = Record<RecentKind, RecentItem[]>;

function read(): Bucket {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { report: [], site: [], document: [], action: [] };
    const parsed = JSON.parse(raw);
    return {
      report: parsed.report ?? [],
      site: parsed.site ?? [],
      document: parsed.document ?? [],
      action: parsed.action ?? [],
    };
  } catch {
    return { report: [], site: [], document: [], action: [] };
  }
}

function write(b: Bucket) {
  try {
    localStorage.setItem(KEY, JSON.stringify(b));
  } catch {
    /* quota / privacy mode — ignore */
  }
}

export function recordRecent(item: Omit<RecentItem, "ts">) {
  const bucket = read();
  const list = bucket[item.kind];
  const next: RecentItem = { ...item, ts: Date.now() };
  const filtered = list.filter((x) => x.id !== item.id);
  filtered.unshift(next);
  bucket[item.kind] = filtered.slice(0, MAX_PER_KIND);
  write(bucket);
}

export function getRecents(kind: RecentKind, limit = 5): RecentItem[] {
  return read()[kind].slice(0, limit);
}

export function getAllRecents(limit = 8): RecentItem[] {
  const b = read();
  const all = [...b.report, ...b.site, ...b.document, ...b.action];
  return all.sort((a, b) => b.ts - a.ts).slice(0, limit);
}

export function clearRecents() {
  write({ report: [], site: [], document: [], action: [] });
}
